import type { JSXElementConstructor, ReactElement } from 'react';
import { useEffect, useRef } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import type { EnhancedSmartLink } from '../utils/smartLinkTypes.ts';

import { useGdrivePromoBanner } from './hooks/useGdrivePromoBanner.tsx';
import { useOrgConnectedAppsPromoBanner } from './hooks/useOrgConnectedAppsPromoBanner.tsx';
import { getBannerNotShownEvent } from './promoBannerAnalytics.ts';

export type ConnectionsByLineMenuFooterProps = {
	links: EnhancedSmartLink[];
	contentId: string | undefined;
};

export const ConnectionsByLineMenuFooter = ({
	links,
	contentId,
}: ConnectionsByLineMenuFooterProps): ReactElement<
	any,
	string | JSXElementConstructor<any>
> | null => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const gdrive = useGdrivePromoBanner({ links, contentId });
	const orgConnectedApps = useOrgConnectedAppsPromoBanner({ links, contentId });

	const element = gdrive.element ?? orgConnectedApps.element ?? null;

	// Settled = rendered or has a terminal reason; both null/undefined = still loading.
	const gdriveSettled = gdrive.element !== null || gdrive.ineligibleReason !== undefined;
	const orgConnectedAppsSettled =
		orgConnectedApps.element !== null || orgConnectedApps.ineligibleReason !== undefined;

	// A shown-then-dismissed banner is covered by the dismissed event, not notShown.
	const hasShownBannerRef = useRef(false);
	useEffect(() => {
		if (element !== null) {
			hasShownBannerRef.current = true;
		}
	}, [element]);

	// Wait for both to settle so an early-settling banner can't drop the other's reason.
	const hasFiredNotShownRef = useRef(false);
	const isSettledNotShown = element === null && gdriveSettled && orgConnectedAppsSettled;
	useEffect(() => {
		if (!isSettledNotShown || hasFiredNotShownRef.current || hasShownBannerRef.current) {
			return;
		}
		hasFiredNotShownRef.current = true;
		createAnalyticsEvent(
			getBannerNotShownEvent({
				gdriveIneligibleReason: gdrive.ineligibleReason,
				orgConnectedAppsIneligibleReason: orgConnectedApps.ineligibleReason,
			}),
		).fire();
	}, [
		element,
		isSettledNotShown,
		gdrive.ineligibleReason,
		orgConnectedApps.ineligibleReason,
		createAnalyticsEvent,
	]);

	return element;
};
